'use client'

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { Button } from '@/components/ui/Button'
import type { FormContent, SolutionSummary } from '@/types/content'
import { contactSchema, type ContactInput } from '@/lib/forms/schemas'
import { CheckboxField, HoneypotField, SelectField, TextareaField, TextField } from './Field'
import { FormFeedback } from './FormFeedback'
import { useFormSubmit } from './useFormSubmit'

interface ContactFormProps {
  content: FormContent
  solutions: SolutionSummary[]
}

/**
 * Formulaire de contact, qui sert aussi aux demandes de démonstration.
 *
 * Pré-remplissage : `/contact?sujet=demo&produit=axia-prod` sélectionne le
 * sujet et le produit. Les valeurs restent MODIFIABLES par le visiteur, et un
 * produit inconnu ou dépublié est ignoré silencieusement
 * (docs/arborescence-cible.md § 5).
 */
export function ContactForm({ content, solutions }: ContactFormProps) {
  const t = useTranslations()
  const searchParams = useSearchParams()
  const { state, submit } = useFormSubmit('/api/forms/contact')

  const subjectParam = searchParams.get('sujet')
  const productParam = searchParams.get('produit')

  const validSubject = content.subjects?.some((option) => option.value === subjectParam)
    ? (subjectParam as ContactInput['subject'])
    : undefined
  const validProduct = solutions.some((solution) => solution.slug === productParam)
    ? (productParam ?? '')
    : ''

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContactInput>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      subject: validSubject ?? 'info',
      product: validProduct,
      consent: false,
    },
  })

  // L'URL peut changer sans remonter le composant (navigation client).
  useEffect(() => {
    if (validSubject) setValue('subject', validSubject)
    setValue('product', validProduct)
  }, [validSubject, validProduct, setValue])

  const onSubmit = handleSubmit(async (values) => {
    const ok = await submit(values)
    if (ok) reset({ subject: 'info', product: '', consent: false })
  })

  if (state === 'success') return <FormFeedback state={state} />

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      <HoneypotField {...register('website')} />

      <div className="grid gap-5 sm:grid-cols-2">
        <TextField
          id="contact-fullName"
          label={content.labels.fullName ?? ''}
          required
          autoComplete="name"
          error={errors.fullName ? t(`form.${errors.fullName.message}`) : undefined}
          {...register('fullName')}
        />
        <TextField
          id="contact-company"
          label={content.labels.company ?? ''}
          autoComplete="organization"
          placeholder={content.placeholders?.company}
          error={errors.company ? t(`form.${errors.company.message}`) : undefined}
          {...register('company')}
        />
        <TextField
          id="contact-email"
          type="email"
          label={content.labels.email ?? ''}
          required
          autoComplete="email"
          error={errors.email ? t(`form.${errors.email.message}`) : undefined}
          {...register('email')}
        />
        <TextField
          id="contact-phone"
          type="tel"
          label={content.labels.phone ?? ''}
          required
          autoComplete="tel"
          error={errors.phone ? t(`form.${errors.phone.message}`) : undefined}
          {...register('phone')}
        />
        <SelectField
          id="contact-subject"
          label={content.labels.subject ?? ''}
          required
          error={errors.subject ? t(`form.${errors.subject.message}`) : undefined}
          {...register('subject')}
        >
          {content.subjects?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>
        <SelectField
          id="contact-product"
          label={content.labels.product ?? ''}
          error={errors.product ? t(`form.${errors.product.message}`) : undefined}
          {...register('product')}
        >
          <option value="">{content.emptyProductLabel}</option>
          {solutions.map((solution) => (
            <option key={solution.id} value={solution.slug}>
              {solution.name}
            </option>
          ))}
        </SelectField>
      </div>

      <TextareaField
        id="contact-message"
        label={content.labels.message ?? ''}
        required
        placeholder={content.placeholders?.message}
        error={errors.message ? t(`form.${errors.message.message}`) : undefined}
        {...register('message')}
      />

      <CheckboxField
        id="contact-consent"
        error={errors.consent ? t(`form.${errors.consent.message}`) : undefined}
        {...register('consent')}
      >
        {content.consentText}{' '}
        <Link href="/confidentialite" className="text-primary-700 underline underline-offset-2">
          {content.privacyLinkLabel}
        </Link>
        .
      </CheckboxField>

      {state === 'error' ? <FormFeedback state={state} /> : null}

      <div>
        <Button
          type="submit"
          size="lg"
          disabled={state === 'submitting'}
          label={state === 'submitting' ? t('form.submitting') : content.submitLabel}
          icon="ArrowRight"
        />
      </div>
    </form>
  )
}
